import Input from "@/components/Input/Input";
import { HStack } from "@/components/Layout";
import { Text } from "@/components/Typography/Text";

import {
  CFormItemBase,
  type CFormItemProps,
  buildCFormItem,
} from "../CFormItem";

type Props = CFormItemProps & {
  value?: number;
  onChange?: (value?: number) => void;
  inputClassName?: string;
  allowNegative?: boolean;
  /** 0-100の範囲に制限するか（デフォルト: true） */
  restrictRange?: boolean;
  /** 小数点以下の最大桁数（デフォルト: 2） */
  maxDecimalPlaces?: number;
  /** ステップ値（デフォルト: 1） */
  step?: number;
};

const PERCENTAGE_REGEX = /\.?0+$/;

/**
 * Card-based Form / PercentageItem
 * パーセンテージを扱うためのフォームアイテム
 */
export const CFormPercentageItem = buildCFormItem((props: Props) => {
  const {
    restrictRange = true,
    maxDecimalPlaces = 2,
    step = 1,
    allowNegative = false,
    ...restProps
  } = props;

  const formatPercentage = (value: number): string => {
    return `${value.toFixed(maxDecimalPlaces).replace(PERCENTAGE_REGEX, "")}%`;
  };

  const minValue = restrictRange
    ? allowNegative
      ? -100
      : 0
    : allowNegative
      ? undefined
      : 0;

  const maxValue = restrictRange ? 100 : undefined;

  return (
    <CFormItemBase
      {...restProps}
      readComponent={
        <Text variant="p">
          {props.value !== undefined ? formatPercentage(props.value) : "-"}
        </Text>
      }
      writeComponent={
        <HStack className="gap-2 items-center">
          <Input
            type="number"
            inputMode="decimal"
            min={minValue}
            max={maxValue}
            step={step}
            value={props.value ?? ""}
            onChange={(e) => {
              const value =
                e.target.value === "" ? undefined : parseFloat(e.target.value);

              // 範囲制限が有効な場合のバリデーション
              if (value !== undefined && restrictRange) {
                const clampedValue = Math.max(
                  minValue ?? -Infinity,
                  Math.min(maxValue ?? Infinity, value),
                );
                props.onChange?.(clampedValue);
              } else {
                props.onChange?.(value);
              }
            }}
            className={props.inputClassName ?? "w-[100px]"}
            disabled={props.readonly}
          />
          <Text variant="labelReg">%</Text>
        </HStack>
      }
    />
  );
});
